import { type MetaFunction, json, redirect, type LoaderFunctionArgs } from "@remix-run/node"
import { Link, type ShouldRevalidateFunction, useLoaderData, useLocation } from "@remix-run/react"
import { Public } from "~/utils/remix"
import Header from "~/components/header"
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar"
import { Separator } from "~/components/ui/separator"
import defaultAvatar from "~/assets/default-avatar.webp"
import type {
  Packages,
  Purchases,
  UserPayment,
  UserSubscription,
  Users,
  VideoBookmark,
  VideoLike,
  Videos,
} from "@prisma/client"
import VideoComments from "~/routes/_app+/videos+/$videoID.comments"
import Typography from "~/components/typography"
import { getImage } from "~/utils/image.server"
import { Likes, LikeButton } from "~/components/likes"
import { useEffect, useRef, useState } from "react"
import { FindUserVideo, FindVideo, StarVideoIDs, StarVideos, VideoVisibility } from "~/models/videos.server"
import { chooseRandom, cleanNulls } from "~/utils/data"
import { VideoGrid } from "~/components/videos"
import { duration } from "~/utils/duration"
import { Button } from "~/components/ui/button"
import { BookmarkIcon, Cross1Icon, PlayIcon } from "@radix-ui/react-icons"
import { UserFollowing } from "~/models/users.server"
import dayjs from "dayjs"
import { CreditState } from "~/purchases/credits.server"
import { GetBundlePointCount, GetSubscriptionPointCount } from "~/utils/legacy-credits.server"
import videoDefault from "~/assets/videos/default.webp"
import { BookmarkButton } from "~/components/bookmarks"
import { GiftButton, RentalButton } from "~/components/rentals"
import { FollowButton } from "~/components/follow"
import { VideoPlayer } from "~/components/video-player"
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs"
import { PurchaseState } from "~/purchases/states"
import { GetLegacySubscriptions } from "~/utils/legacy-subscriptions.server"
import { cn } from "~/utils/shadecn"
import cookie from "cookie"
import { LoveNoteButton } from "~/components/love-notes/loveNote"
import { commitUserSession, getUserSession } from "~/utils/session.server"

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: `Real World Sex Video: ${data?.video?.VideoTitle}` },
    {
      property: "og:title",
      content: `Real World Sex Video: ${data?.video?.VideoTitle}`,
    },
    { name: "description", content: data?.video?.VideoDescription },
  ]
}

export const loader = Public(async ({ request, context, params }: LoaderFunctionArgs) => {
  const user = context.user
  const url = new URL(request.url)
  let source: string | null = null
  let sourceID: number | null = null

  if (url.searchParams.get("s")) {
    try {
      source = url.searchParams.get("s")
      sourceID = parseInt(url.searchParams.get("id")!)

      if (user && source) {
        const headers = new Headers()
        headers.append(
          "Set-Cookie",
          `Source=${source}-${sourceID};path=/videos/${params.videoID};domain=${
            process.env.MLNP_DOMAIN!.indexOf("localhost") >= 0 ? "localhost" : `.${process.env.MLNP_DOMAIN}`
          }`
        )
        await context.db.videoViewSources.create({
          data: {
            Source: source,
            SourceID: sourceID,
            UserID: user.UserID,
            VideoID: parseInt(params.videoID!),
          },
        })

        return redirect(`/videos/${params.videoID}`, { headers })
      }
    } catch (e) {}
  }

  if (url.searchParams.get("utm_campaign")) {
    source = "utm"
    sourceID = 0
  } else if (request.headers.get("Cookie")) {
    const cookieHeader = cookie.parse(request.headers.get("Cookie")!)
    if (cookieHeader.Source) {
      const result = cookieHeader.Source.split("-")
      source = result[0]
      sourceID = parseInt(result[1])
    }
  }

  if (!params.videoID) {
    throw json({ video: null }, { status: 404 })
  }

  if (isNaN(parseInt(params.videoID))) {
    throw redirect("/videos")
  }

  let video = await FindVideo(context, parseInt(params.videoID))
  if (!video && user) {
    video = await FindUserVideo(context, parseInt(params.videoID))
  }
  if (!video) {
    throw json({ video: null }, { status: 404 })
  }

  const otherVideoIDs = chooseRandom(
    await StarVideoIDs(context, video.UserID!, {
      VideoID: { not: video.VideoID },
    }),
    10
  ).map((v) => v.VideoID)
  let otherVideos = await StarVideos(
    context,
    video.UserID!,
    1,
    { VideoID: { in: chooseRandom(otherVideoIDs, 10) } },
    10
  )
  otherVideos = otherVideos.sort((a, b) => {
    return otherVideoIDs.indexOf(a.VideoID) - otherVideoIDs.indexOf(b.VideoID)
  })

  const likeCount = await context.db.videoLike.count({
    where: { VideoID: video.VideoID },
  })

  let sub: ({ Package: Packages } & Purchases) | null = null
  let legacySubs: (({ User: Users | null; GifteeUser: Users | null } & UserSubscription) | null)[] = []
  let bookmark: VideoBookmark | null = null
  let like: VideoLike | null = null
  let following: { StarID: bigint; UserID: bigint; CreatedAt: Date } | null = null
  let rentCreatedAt = null
  let rentExpiry = null
  let credits = 0
  let cards: UserPayment[] = []
  let pkg: Packages | null = null
  let loveNotePkgs: Packages[] = []
  let loveObjectPkgs: Packages[] = []
  let pkgs: Packages[] = []

  if (user) {
    sub = await context.db.purchases.findFirst({
      where: {
        UserID: user.UserID,
        State: { not: { in: [PurchaseState.Init, PurchaseState.Cancelled] } },
        Package: {
          CreditsToAward: { gt: 0 },
          RecurInterval: { not: null },
        },
      },
      include: { Package: true },
    })

    if (!sub) {
      legacySubs = (await GetLegacySubscriptions(context, user.UserID)).filter((sub) => sub?.Type === 1)
    }

    bookmark = await context.db.videoBookmark.findFirst({
      where: {
        UserID: user.UserID,
        VideoID: video.VideoID,
      },
    })

    like = await context.db.videoLike.findFirst({
      where: {
        OR: [
          { UserID: user.UserID },
          {
            AND: [{ CognitoUserID: { not: null } }, { CognitoUserID: user.CognitoUserID }],
          },
        ],
        VideoID: video.VideoID,
      },
    })

    following = await UserFollowing(context, video.UserID!)

    if (user.Role == 1 || user.UserID == video.UserID) {
      rentCreatedAt = new Date()
      rentExpiry = dayjs().add(1, "month").toDate()
    } else {
      const userRent = await context.db.userRent.findFirst({
        where: {
          VideoID: video.VideoID,
          UserID: user.UserID,
          CreatedAt: { gte: dayjs().subtract(1, "month").toDate() },
          Deleted: 0,
        },
      })

      if (userRent) {
        rentCreatedAt = userRent.CreatedAt
        rentExpiry = dayjs(rentCreatedAt).add(1, "month").toDate()
      } else {
        const rental = await context.db.rentals.findFirst({
          where: {
            VideoID: video.VideoID,
            UserID: user.UserID,
            ExpiresAt: { gte: new Date() },
          },
        })

        if (rental) {
          rentCreatedAt = rental.CreatedAt
          rentExpiry = rental.ExpiresAt
        }
      }
    }

    cards = await context.db.userPayment.findMany({
      where: { UserID: user.UserID, CardStatus: 1, Deleted: 0 },
    })

    pkg = await context.db.packages.findFirst({
      where: { Tag: "pay-as-you-go" },
    })

    pkgs = await context.db.packages.findMany({
      where: { Tag: { in: ["quickie", "daymaker", "binge"] } },
    })

    loveNotePkgs = await context.db.packages.findMany({
      where: { Tag: { startsWith: "love-note" } },
    })

    loveObjectPkgs = await context.db.packages.findMany({
      where: { Tag: { startsWith: "object" } },
    })

    const purchaseCredits = await context.db.credits.aggregate({
      where: {
        UserID: user.UserID,
        State: CreditState.Available,
        ExpiresAt: { gt: dayjs().toDate() },
      },
      _count: true,
    })

    const subPoints = await GetSubscriptionPointCount(context)
    const bundlePoints = await GetBundlePointCount(context)

    credits = purchaseCredits._count + subPoints + bundlePoints
  }

  return json({
    user,
    hasSub: sub || legacySubs.length > 0,
    source: source,
    sourceID: sourceID,
    currentUserAvatar: user && getImage(user.Avatar, defaultAvatar),
    otherVideos: otherVideos.map((video) => ({
      VideoID: video.VideoID,
      VideoTitle: video.VideoTitle,
      VideoDescription: video.VideoDescription,
      VideoDuration: video.VideoDuration,
      Slug: video.Slug,
      Status: video.Status,
      Visibility: VideoVisibility(context, video.User, video),
      MarqueeTags: video.VideoMarqueeTags.map((tag) => ({
        MarqueeTagID: tag.MarqueeTag?.MarqueeTagID,
        Name: tag.MarqueeTag?.Name,
        Slug: tag.MarqueeTag?.Slug,
      })),
      Tags: video.ActualVideoTags.map((tag) => ({
        TagID: tag.Tag?.TagID,
        Name: tag.Tag?.Name,
        Slug: tag.Tag?.Slug,
      })),
      StarID: video.UserID,
      StarName: video.User?.UserName,
      StarDisplayName: video.User?.DisplayName,
      StarAvatar: getImage(video.User?.Avatar, defaultAvatar),
      Stills: JSON.parse(cleanNulls(video.Stills, "[]")).map((still: any) =>
        getImage(JSON.stringify(still), videoDefault)
      ),
      VideoDomain: process.env.CLOUDFRONT_DOMAIN,
      BlurredStill: video.BlurredStill,
      Content: video.Content,
      PublishedAt: video.PublishedAt,
    })),
    video: {
      VideoID: video.VideoID,
      VideoTitle: video.VideoTitle,
      VideoDescription: video.VideoDescription,
      VideoDuration: video.VideoDuration,
      Slug: video.Slug,
      Status: video.Status,
      Visibility: VideoVisibility(context, video.User, video),
      MarqueeTags: video.VideoMarqueeTags.map((tag) => ({
        MarqueeTagID: tag.MarqueeTag?.MarqueeTagID,
        Name: tag.MarqueeTag?.Name,
        Slug: tag.MarqueeTag?.Slug,
      })),
      Tags: video.ActualVideoTags.map((tag) => ({
        TagID: tag.Tag?.TagID,
        Name: tag.Tag?.Name,
        Slug: tag.Tag?.Slug,
      })),
      StarID: video.UserID,
      StarName: video.User?.UserName,
      StarDisplayName: video.User?.DisplayName,
      StarAvatar: getImage(video.User?.Avatar, defaultAvatar),
      Stills: JSON.parse(cleanNulls(video.Stills, "[]")).map((still: any) =>
        getImage(JSON.stringify(still), videoDefault)
      ),
      IntroURL: `/videos/${video.VideoID}.m3u8`,
      VideoIntroFlipped: video.VideoIntroFlipped,
      IntroCaptions: video.VideoIntroCaptions,
      VideoURL: user && `/videos/${video.VideoID}.m3u8?full=true`,
      VideoCastURL:
        user &&
        `/videos/${video.VideoID}.m3u8?session=${(await commitUserSession(await getUserSession(request))).replace("mlnp_session=", "").split(";")[0]}`,
      VideoRealFlipped: video.VideoRealFlipped,
      VideoCaptions: user && rentExpiry && video.VideoRealCaptions,
      VideoDomain: process.env.CLOUDFRONT_DOMAIN,
      BlurredStill: video.BlurredStill,
      Content: video.Content,
      PublishedAt: video.PublishedAt,
    },
    follow: following,
    rentExpiry: rentExpiry,
    singlePkg: pkg,
    loveNotePkgs: loveNotePkgs,
    loveObjectPkgs: loveObjectPkgs,
    pkgs: {
      quickie: pkgs.find((pkg) => pkg.Tag === "quickie"),
      daymaker: pkgs.find((pkg) => pkg.Tag === "daymaker"),
      binge: pkgs.find((pkg) => pkg.Tag === "binge"),
    },
    cards: cards,
    credits: credits,
    bookmarkID: bookmark?.BookmarkID,
    likeID: like?.LikeID,
    likeCount,
  })
})

export const shouldRevalidate: ShouldRevalidateFunction = (args) => {
  if (args.actionResult?.revalidate === false) {
    return false
  }
  return args.defaultShouldRevalidate
}

export default function Video() {
  const _data = useLoaderData<typeof loader>()
  const location = useLocation()
  const videoRef = useRef(null)
  const [data, setData] = useState(_data)
  const [video, setVideo] = useState(_data.video)
  const [otherVideos, setOtherVideos] = useState(_data.otherVideos)
  const [rentExpiry, setRentExpiry] = useState(_data.rentExpiry)
  const [follow, setFollow] = useState(_data.follow)
  const [bookmarkID, setBookmarkID] = useState(_data.bookmarkID)
  const [likeID, setLikeID] = useState(_data.likeID)
  const [likeCount, setLikeCount] = useState(_data.likeCount)
  const [preview, setPreview] = useState((!data.rentExpiry).toString())
  const [videoURL, setVideoURL] = useState(data.rentExpiry ? video.VideoURL : video.IntroURL)
  const [videoType, setVideoType] = useState(_data.rentExpiry ? "full" : "intro")
  const [videoCastURL, setVideoCastURL] = useState(data.rentExpiry ?? video.VideoCastURL)
  const [videoFlipped, setVideoFlipped] = useState(data.rentExpiry ? video.VideoRealFlipped : video.VideoIntroFlipped)
  const [videoCaptions, setVideoCaptions] = useState(data.rentExpiry ? video.VideoCaptions : video.IntroCaptions)
  const [rentalOpen, setRentalOpen] = useState(false)
  const [previewVideoSelected, setPreviewVideoSelected] = useState(false)
  const [videoHasEnded, setVideoHasEnded] = useState(false)
  const [previewEnded, setPreviewEnded] = useState(false)
  const [previewCtaTime, setPreviewCtaTime] = useState(false)
  const [showPreviewCta, setShowPreviewCta] = useState(false)
  const [refreshComments, setRefreshComments] = useState(false)

  function handleVideoToggle(value: string) {
    setPreview(value)
    setVideoURL(value === "false" ? data.video.VideoURL : data.video.IntroURL)
    setVideoType(value === "false" ? "full" : "intro")
    setVideoCastURL(value === "false" ?? data.video.VideoCastURL)
    setVideoCaptions(value === "false" ? data.video.VideoCaptions : data.video.IntroCaptions)
  }

  useEffect(() => {
    setData(_data)
    setVideo(_data.video)
    setOtherVideos(_data.otherVideos)
    setRentExpiry(_data.rentExpiry)
    setFollow(_data.follow)
    setBookmarkID(_data.bookmarkID)
    setLikeID(_data.likeID)
    setLikeCount(_data.likeCount)
    setPreview((!_data.rentExpiry).toString())
    setVideoURL(_data.rentExpiry ? _data.video.VideoURL : _data.video.IntroURL)
    setVideoType(_data.rentExpiry ? "full" : "intro")
    setVideoCastURL(_data.rentExpiry ?? _data.video.VideoCastURL)
  }, [_data])

  const onToggleVideo = (visible: boolean) => {
    setPreviewVideoSelected(preview === "true" && visible)
    setVideoHasEnded(false)
    setPreviewEnded(false)
  }

  const videoEnded = async () => {
    setVideoHasEnded(true)

    if (!rentExpiry && data.user) {
      try {
        const response = await fetch("/api/recently-viewed", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userID: data.user.UserID, videoID: video.VideoID }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          console.error("Failed to track video view:", errorData.error)
        }
      } catch (error) {
        console.error("Failed to track video view:", error)
      }
    }
  }

  const videoCta = (curTime: number) => {
    setPreviewCtaTime(curTime >= 7 && curTime <= 12)
  }

  useEffect(() => {
    setPreviewEnded(!rentExpiry && videoHasEnded && previewVideoSelected)
  }, [rentExpiry, videoHasEnded, previewVideoSelected])

  useEffect(() => {
    setShowPreviewCta(!rentExpiry && previewVideoSelected && !previewEnded && previewCtaTime)
  }, [rentExpiry, previewEnded, previewVideoSelected, previewCtaTime])

  return (
    <div className="max-w-5xl m-auto mt-16">
      <Header title={video.VideoTitle} separator={false} />
      {data.user && rentExpiry && (
        <Tabs
          value={preview}
          onValueChange={handleVideoToggle}
          className="flex justify-center sm:mx-4 md:pt-1 md:pb-1 px-4 sm:rounded-t-lg bg-muted">
          <TabsList>
            <TabsTrigger value={"true"}>View Preview and Stills</TabsTrigger>
            <TabsTrigger value={"false"}>View Full Video</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      <div className="sm:mx-4">
        {data.user && video.Status === 0 && (
          <Typography className="prose-sm py-0.5 px-1.5 my-4 text-center bg-amber-200 dark:bg-yellow-500 text-foreground dark:text-background rounded-sm italic">
            <p>
              <b>Video Pending:</b> Thanks for submitting your video! Hang tight while our curation team reviews it
            </p>
          </Typography>
        )}
        <div className="aspect-video relative">
          {data.user || video.Visibility === 0 ?
            <>
              <div className="absolute w-full aspect-video overflow-hidden">
                <div
                  className={cn(
                    "opacity-0 absolute top-0 w-full aspect-video flex items-center justify-center z-20 bg-background/80 left-full transition-opacity duration-1000",
                    previewEnded && "opacity-100 -translate-x-full"
                  )}>
                  <Typography className="text-center">
                    <h2 className="text-lg sm:text-xl">Enjoy this free peek? Enjoy even more...</h2>
                    {data.user ?
                      <RentalButton
                        text="Rent the full video now!"
                        user={data.user}
                        sub={data.hasSub}
                        video={video}
                        source={data.source}
                        sourceID={data.sourceID}
                        starName={video.StarName}
                        rentExpiry={rentExpiry}
                        pkg={data.singlePkg}
                        pkgs={data.pkgs}
                        cards={data.cards}
                        credits={data.credits}
                        open={rentalOpen}
                        setOpen={setRentalOpen}
                      />
                    : <Link to="/register">
                        <Button className="px-4 py-4 text-md">
                          <PlayIcon className="mr-2 h-4 w-4" />
                          Rent the full video now!
                        </Button>
                      </Link>
                    }
                  </Typography>
                </div>
              </div>
              <VideoPlayer
                ref={videoRef}
                user={data.user}
                video={video}
                videoType={videoType}
                flipped={videoFlipped}
                src={videoURL}
                castSrc={videoCastURL}
                captions={videoCaptions}
                rotateStills={preview === "true"}
                onToggleVideo={onToggleVideo}
                onEnded={videoEnded}
                onTimeUpdate={videoCta}
                autoplay={true}
                className={cn("rounded-none transition-opacity duration-1000 w-full", previewEnded && "opacity-5")}
              />
            </>
          : <div className="relative w-full aspect-video group flex items-center justify-center">
              <div className="absolute top-0 left-0 bottom-2 right-0 rounded-t-md overflow-clip -z-10">
                <img
                  src={video.BlurredStill}
                  loading="lazy"
                  className="absolute top-0 left-0 w-full object-cover scale-[1.12]"
                />
              </div>
              <div className="text-center bg-black/20 p-2 sm:p-4 sm:rounded-lg text-sm sm:text-xl">
                This MakeLoveNotPornstar has chosen to only make their videos discoverable by the community.
                <br /> <br />
                <Link to={`/register?redirectTo=${location.pathname}`}>
                  <Button size="lg" className="text-sm h-9 sm:text-lg sm:h-12">
                    Join here
                  </Button>
                </Link>
              </div>
            </div>
          }
        </div>
        <div></div>
        <div className="px-3 pb-2.5 pt-2.5 mb-2 -mt-2 flex justify-between flex-wrap items-center bg-muted sm:rounded-b-lg gap-y-1">
          <div className="flex justify-start items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={video.StarAvatar} className="object-cover" />
              <AvatarFallback>{video.StarDisplayName}</AvatarFallback>
            </Avatar>
            <div className="text-lg font-medium">
              <Link to={`/makelovenotpornstars/${video.StarName}`}>{video.StarDisplayName}</Link>
            </div>
            {data.user ?
              <>
                <FollowButton className="px-3 py-0 h-6 text-xs/3 rounded-xl" starID={video.StarID} follow={follow} />
              </>
            : <Link to="/register">
                <Button className="px-3 py-0 h-6 text-xs/3 rounded-xl" type="submit">
                  Follow
                </Button>
              </Link>
            }
          </div>

          <div className="flex justify-end items-center mt-2 md:mt-0 w-full sm:w-auto">
            <div className="flex flex-row sm:overflow-visible overflow-x-auto sm:gap-4 gap-2 w-full sm:w-auto scrollbar-hide">
              {data.user ?
                <>
                  <BookmarkButton videoID={video.VideoID} bookmarkID={bookmarkID} />
                  <RentalButton
                    user={data.user}
                    sub={data.hasSub}
                    video={video}
                    source={data.source}
                    sourceID={data.sourceID}
                    starName={video.StarName}
                    rentExpiry={rentExpiry}
                    pkg={data.singlePkg}
                    pkgs={data.pkgs}
                    cards={data.cards}
                    credits={data.credits}
                    open={rentalOpen}
                    setOpen={setRentalOpen}
                  />
                </>
              : <>
                  <Link to="/register">
                    <Button variant="outline" className="border-foreground/30">
                      <BookmarkIcon className="mr-2 h-4 w-4" />
                      Bookmark
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="px-4 py-4 text-md">
                      <PlayIcon className="mr-2 h-4 w-4" />
                      Rent
                    </Button>
                  </Link>
                </>
              }
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-center mx-2 sm:mx-0 gap-4 overflow-hidden px-1">
          <div className="flex justify-start items-center gap-2 sm:gap-4 overflow-x-auto w-full sm:w-auto scrollbar-hide">
            <Likes likeCount={likeCount} />
            {data.user ?
              <>
                <LoveNoteButton
                  user={data.user}
                  avatar={data.currentUserAvatar}
                  video={video}
                  pkgs={data.loveNotePkgs}
                  loveObjects={data.loveObjectPkgs}
                  cards={data.cards}
                  setPurchase={(p) => setRefreshComments(true)}
                />
                <GiftButton video={video} pkg={data.singlePkg} cards={data.cards} credits={data.credits} />
                <LikeButton
                  videoID={_data.video.VideoID}
                  likeID={likeID}
                  setLikeID={setLikeID}
                  setLikeCount={setLikeCount}
                  likeCount={likeCount}
                  disabled={!data.user}
                />
              </>
            : <>
                <LikeButton
                  videoID={_data.video.VideoID}
                  likeID={likeID}
                  setLikeID={setLikeID}
                  setLikeCount={setLikeCount}
                  likeCount={likeCount}
                  disabled={!data.user}
                />
              </>
            }
          </div>
          <p className="text-muted-foreground text-md text-right">
            RealWorldSexTime: <b>{duration(video.VideoDuration)}</b>
          </p>
        </div>
        <Separator className="my-2" />
        <Typography className="mt-4 leading-7 mx-4 sm:mx-0">{video.VideoDescription}</Typography>
        {(video.Tags?.length > 0 || video.MarqueeTags?.length > 0) && (
          <Typography>
            <p className="mt-2 mb-1 sm:mb-2 leading-7 text-md text-muted-foreground mx-4 sm:mx-0">
              <span>Tags: </span>
              {video.MarqueeTags?.length > 0 &&
                video.MarqueeTags.map((tag: any, index: number) => (
                  <span key={index}>
                    <Link to={`/videos/tag/${tag.Name}`}>{tag.Name}</Link>{" "}
                  </span>
                ))}
              {video.Tags?.length > 0 &&
                video.Tags.map((tag: any, index: number) => (
                  <span key={index}>
                    <Link to={`/videos/tag/${tag.Name}`}>{tag.Name}</Link>{" "}
                  </span>
                ))}
            </p>
          </Typography>
        )}
        <div id="comments" className="grid grid-cols-1 sm:grid-cols-3 gap-4 mx-4 mt-8 sm:mx-0">
          <VideoComments
            className="sm:col-span-2"
            currentUser={data.user}
            currentUserAvatar={data.currentUserAvatar}
            videoID={video.VideoID}
            disabled={!data.user}
            refresh={refreshComments}
            setRefresh={setRefreshComments}
          />
          {otherVideos.length > 0 && (
            <OtherVideos user={data.user} videos={otherVideos} source={"OtherVideos"} sourceID={video.VideoID} />
          )}
        </div>
      </div>
    </div>
  )
}

const OtherVideos = ({
  user,
  videos,
  source,
  sourceID,
  className,
}: {
  user: Users
  videos: Videos[]
  source: string
  sourceID: number
  className?: string
}) => {
  return (
    <div className={className}>
      <Typography>
        <h2>Other Videos</h2>
      </Typography>
      <Separator className="mt-4 mb-4" />
      <VideoGrid
        loggedIn={!!user}
        videos={videos}
        source={source}
        sourceID={sourceID}
        videoClassNames="h-auto w-full"
      />
    </div>
  )
}
